import { useEffect, useRef, useState, type FormEvent } from 'react';
import { PantoneAutocomplete } from '../components/PantoneAutocomplete';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useAuth, can } from '../lib/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  useClients, usePrintingMachines, useAllMachines, machineOptions, usePaperTypes, useTopFinishes, type LookupOption,
} from '../lib/lookups';

interface ProductForm {
  name: string; client: string; artwork: string; dieNumber: string;
  plateNumber: string; noOfUps: string; productRate: string;
  paperType: string; sheetSize: string;
  sheetFullSizeLength: string; sheetFullSizeWidth: string; sheetGsm: string;
  sheetCutSizeLength: string; sheetCutSizeWidth: string;
  cutToCutLength: string; cutToCutWidth: string;
  laminationType: string;
  productLength: string; productWidth: string; productHeight: string;
  foilingColor: string; filmSizeMicron: string; embossingNo: string;
  shadeCardNo: string; prePostLamination: string;
  onlineEmbossing: boolean; offlineEmbossing: boolean;  braille: boolean;
  spotUv: boolean; screenUv: boolean; offlineCoating: boolean; onlineCoating: boolean; baseCoat: boolean;
  cartonType: string; metallicCarton: boolean; threePlyCarton: boolean;
  machineFront: string; machineFrontColor: string;
  machineFront2: string; machineFront2Color: string;
  machineBack: string; machineBackColor: string;
  artWNo: string; bpNo: string; remark: string; partyGsm: string;
}

const empty: ProductForm = {
  name: '', client: '', artwork: '', dieNumber: '',
  plateNumber: '', noOfUps: '', productRate: '',
  paperType: '', sheetSize: '',
  sheetFullSizeLength: '', sheetFullSizeWidth: '', sheetGsm: '',
  sheetCutSizeLength: '', sheetCutSizeWidth: '',
  cutToCutLength: '', cutToCutWidth: '',
  laminationType: '',
  productLength: '', productWidth: '', productHeight: '',
  foilingColor: '', filmSizeMicron: '', embossingNo: '',
  shadeCardNo: '', prePostLamination: '',
  onlineEmbossing: false, offlineEmbossing: false, braille: false,
  spotUv: false, screenUv: false, offlineCoating: false, onlineCoating: false, baseCoat: false,
  cartonType: '', metallicCarton: false, threePlyCarton: false,
  machineFront: '', machineFrontColor: '',
  machineFront2: '', machineFront2Color: '',
  machineBack: '', machineBackColor: '',
  artWNo: '', bpNo: '', remark: '', partyGsm: '',
};

export function ProductFormPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<ProductForm>(empty);
  const [error, setError] = useState<string | null>(null);
  const [dieLookupHint, setDieLookupHint] = useState<string | null>(null);
  const [dieNoHint, setDieNoHint] = useState<string | null>(null);

  const clients = useClients();
  const machines = usePrintingMachines();
  const allMachines = useAllMachines();
  const papers = usePaperTypes();
  const finishes = useTopFinishes();

  const existing = useQuery({
    enabled: isEdit,
    queryKey: ['product', id],
    queryFn: () => api<ProductForm & {
      id: string; orderCount: number;
      // Who last filled the board spec (cut size, GSM, paper type, top finish).
      boardSpecByName?: string | null; boardSpecOn?: string | null; boardSpecPo?: string | null;
    }>(`/api/products/${id}`),
  });

  useEffect(() => {
    if (existing.data) setForm({ ...empty, ...existing.data });
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>(isEdit ? `/api/products/${id}` : '/api/products', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      navigate(listReturnUrl('/products'));
    },
    onError: (e) => setError((e as Error).message),
  });

  function update<K extends keyof ProductForm>(k: K, v: ProductForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  /**
   * Reverse of lookupByDesign — the operator often knows the Die No first
   * (it is written on the physical die), and the Die Master already carries
   * the design no, ups, sheet cut size and product dimensions.  Only empty
   * fields are filled so a manual override is never clobbered.
   */
  async function lookupByDie(dieNo: string) {
    if (!dieNo.trim()) return;
    // Deliberately runs on Edit too: the "only fill empty" logic below
    // protects any value the operator already put in, so a legacy product
    // with blank spec rows still benefits from a fresh Die Master lookup.
    setDieNoHint('Looking up…');
    try {
      const die = await api<{
        dieNo: string; designNo: string; dieUps: string;
        sheetCutSizeLength: string; sheetCutSizeWidth: string;
        productLength: string; productWidth: string; productHeight: string;
      } | null>(
        `/api/die-designs/by-die-no/${encodeURIComponent(dieNo.trim())}`,
      );
      if (!die) { setDieNoHint('No Die Master record for this Die No.'); return; }
      setForm((f) => {
        const next: ProductForm = { ...f };
        // Only fill fields that are still empty — the operator may have
        // intentionally set a different value already.
        if (!next.artwork            && die.designNo)           next.artwork            = die.designNo;
        if (!next.noOfUps            && die.dieUps)             next.noOfUps            = die.dieUps;
        if (!next.sheetCutSizeLength && die.sheetCutSizeLength) next.sheetCutSizeLength = die.sheetCutSizeLength;
        if (!next.sheetCutSizeWidth  && die.sheetCutSizeWidth)  next.sheetCutSizeWidth  = die.sheetCutSizeWidth;
        if (!next.productLength      && die.productLength)      next.productLength      = die.productLength;
        if (!next.productWidth       && die.productWidth)       next.productWidth       = die.productWidth;
        if (!next.productHeight      && die.productHeight)      next.productHeight      = die.productHeight;
        return next;
      });
      setDieNoHint('Auto-filled Design No, Ups, Sheet Cut Size and Product L×W×H from Die Master.');
    } catch {
      setDieNoHint('Lookup failed.');
    }
  }

  async function lookupByDesign(designNo: string) {
    if (!designNo.trim() || isEdit) return;
    setDieLookupHint('Looking up…');
    try {
      // Strict pull-through from Die Master ONLY. The same design can be used
      // on different products with different paper / finishing / machine
      // colors, so a prior product with the same artwork is NOT a safe seed
      // for anything except what the Die Master actually stores (die no,
      // ups, sheet cut size).
      const die = await api<{
        dieNo: string;
        sheetCutSizeLength: string; sheetCutSizeWidth: string;
        dieUps: string;
      } | null>(
        `/api/die-designs/by-design-no/${encodeURIComponent(designNo)}`,
      );
      setForm((f) => {
        const next: ProductForm = { ...f };
        if (die) {
          if (die.dieNo)              next.dieNumber          = die.dieNo;
          if (die.dieUps)             next.noOfUps            = die.dieUps;
          if (die.sheetCutSizeLength) next.sheetCutSizeLength = die.sheetCutSizeLength;
          if (die.sheetCutSizeWidth)  next.sheetCutSizeWidth  = die.sheetCutSizeWidth;
        }
        next.artwork = designNo;
        return next;
      });
      setDieLookupHint(die
        ? 'Auto-filled Die No, Ups and Sheet Cut Size from Die Master.'
        : 'No Die Master record for this Design No.');
    } catch {
      setDieLookupHint('Lookup failed.');
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError('Name is required.'); return; }
    save.mutate();
  }

  // Purchase (and CRM) may read specifications but not change them. `fieldset
  // disabled` makes the whole form non-interactive in one place, so a new field
  // added later cannot accidentally stay editable.
  const canEditProducts = can(user, isEdit ? 'EDIT_PRODUCT' : 'CREATE_PRODUCT');

  if (isEdit && existing.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {!canEditProducts ? `Product — ${id}` : isEdit ? `Edit Product — ${id}` : 'New Product'}
          {existing.data?.orderCount ? (
            <span className="ml-3 text-xs font-normal text-slate-600">({existing.data.orderCount} past job orders)</span>
          ) : null}
        </h1>
        <Link to={listReturnUrl('/products')} className="text-sm text-slate-600 hover:underline">← Back</Link>
      </div>

      {!canEditProducts && (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          View only — specifications are maintained by Design.
        </div>
      )}

      <form className="space-y-4" onSubmit={onSubmit}>
        <fieldset disabled={!canEditProducts} className="space-y-4 disabled:opacity-100">
        <Section title="Basic">
          <F label="Product Name *" value={form.name} onChange={(v) => update('name', v)} autoFocus />
          <Sel label="Client" value={form.client} onChange={(v) => update('client', v)} options={clients.data ?? []} />
          <div>
            <label className="label">Die No</label>
            <input className="input" value={form.dieNumber}
              onChange={(e) => update('dieNumber', e.target.value)}
              onBlur={(e) => lookupByDie(e.target.value)} />
            {dieNoHint && <p className="text-xs text-slate-500 mt-1">{dieNoHint}</p>}
          </div>
          <div>
            <label className="label">Design No (Artwork)</label>
            <input className="input" value={form.artwork}
              onChange={(e) => update('artwork', e.target.value)}
              onBlur={(e) => lookupByDesign(e.target.value)} />
            {dieLookupHint && <p className="text-xs text-slate-500 mt-1">{dieLookupHint}</p>}
          </div>
          <F label="A/W Material No" value={form.artWNo} onChange={(v) => update('artWNo', v)} />
          <F label="Plate Number" value={form.plateNumber} onChange={(v) => update('plateNumber', v)} />
          <F label="No. of Ups" value={form.noOfUps} onChange={(v) => update('noOfUps', v)} />
          <F label="Product Rate" value={form.productRate} onChange={(v) => update('productRate', v)} />
          <F label="Party GSM" value={form.partyGsm} onChange={(v) => update('partyGsm', v)} />
        </Section>

        <Section title="Paper & Sheet">
          {existing.data?.boardSpecByName && (
            <p className="md:col-span-3 text-xs text-slate-500">
              Cut size, GSM, paper type and top finish filled by <b>{existing.data.boardSpecByName}</b>
              {existing.data.boardSpecOn && <> on {new Date(existing.data.boardSpecOn).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>}
              {existing.data.boardSpecPo ? <> — with the artwork for PO {existing.data.boardSpecPo}</> : <> — here in Product Master</>}.
            </p>
          )}
          <Sel label="Paper Type" value={form.paperType}
            onChange={(v) => update('paperType', v)}
            options={papers.data ?? []} />
          {/* The Sheet master became noise — enter the full sheet size
              directly instead.  Length, width and GSM are stored in
              their own product columns; the sheetSize label above is
              still assembled server-side so downstream reads keep
              working while the Sheet_Size FK stays as a legacy
              fallback for products that never got these filled in. */}
          <F label="Full Sheet Length (mm)" value={form.sheetFullSizeLength} onChange={(v) => update('sheetFullSizeLength', v)} />
          <F label="Full Sheet Width (mm)"  value={form.sheetFullSizeWidth}  onChange={(v) => update('sheetFullSizeWidth', v)} />
          <F label="Sheet GSM"              value={form.sheetGsm}            onChange={(v) => update('sheetGsm', v)} />
          <Sel label="Lamination Type" value={form.laminationType} onChange={(v) => update('laminationType', v)} options={finishes.data ?? []} />
          <F label="Sheet Cut Size Length (mm)" value={form.sheetCutSizeLength} onChange={(v) => update('sheetCutSizeLength', v)} />
          <F label="Sheet Cut Size Width (mm)" value={form.sheetCutSizeWidth} onChange={(v) => update('sheetCutSizeWidth', v)} />
          <F label="K to K Size Length (mm)" value={form.cutToCutLength} onChange={(v) => update('cutToCutLength', v)} />
          <F label="K to K Size Width (mm)" value={form.cutToCutWidth} onChange={(v) => update('cutToCutWidth', v)} />
          <F label="Product Length (mm)" value={form.productLength} onChange={(v) => update('productLength', v)} />
          <F label="Product Width (mm)" value={form.productWidth} onChange={(v) => update('productWidth', v)} />
          <F label="Product Height (mm)" value={form.productHeight} onChange={(v) => update('productHeight', v)} />
        </Section>

        <Section title="Finishing">
          <F label="Foiling Color" value={form.foilingColor} onChange={(v) => update('foilingColor', v)} />
          <F label="Film Size & Micron" value={form.filmSizeMicron} onChange={(v) => update('filmSizeMicron', v)} />
          <F label="Embossing No" value={form.embossingNo} onChange={(v) => update('embossingNo', v)} />
          <F label="Shade Card No" value={form.shadeCardNo} onChange={(v) => update('shadeCardNo', v)} />
          <div>
            <label className="label">Pre / Post Lamination</label>
            <select className="input" value={form.prePostLamination}
              onChange={(e) => update('prePostLamination', e.target.value)}>
              <option value="">—</option>
              <option value="Pre">Pre</option>
              <option value="Post">Post</option>
            </select>
          </div>
          <Bools form={form} update={update} />
        </Section>

        <Section title="Carton">
          <F label="Type" value={form.cartonType} onChange={(v) => update('cartonType', v)} />
          <div className="md:col-span-2 grid gap-2 sm:grid-cols-2 self-end pb-1">
            {([['metallicCarton', 'Metallic Carton'], ['threePlyCarton', '3-Ply Carton']] as const).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form[key]}
                  onChange={(e) => update(key, e.target.checked)} />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </Section>

        <Section title="Machine">
          <Sel label="Machine Front" value={form.machineFront} onChange={(v) => update('machineFront', v)} options={machineOptions(machines.data, allMachines.data, form.machineFront)} />
          <PantoneAutocomplete label="Front Color" value={form.machineFrontColor} onChange={(v) => update('machineFrontColor', v)} />
          <Sel label="Machine Front 2" value={form.machineFront2} onChange={(v) => update('machineFront2', v)} options={machineOptions(machines.data, allMachines.data, form.machineFront2)} />
          <PantoneAutocomplete label="Front Color 2" value={form.machineFront2Color} onChange={(v) => update('machineFront2Color', v)} />
          <Sel label="Machine Back" value={form.machineBack} onChange={(v) => update('machineBack', v)} options={machineOptions(machines.data, allMachines.data, form.machineBack)} />
          <PantoneAutocomplete label="Back Color" value={form.machineBackColor} onChange={(v) => update('machineBackColor', v)} />
          <F label="BP No (Back Print Plate No)" value={form.bpNo} onChange={(v) => update('bpNo', v)} />
        </Section>

        <div className="card">
          <div className="card-header">Remark</div>
          <div className="card-body">
            <textarea className="input min-h-[100px]" value={form.remark}
              onChange={(e) => update('remark', e.target.value)} />
          </div>
        </div>

        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        </fieldset>

        {/* Outside the fieldset: uploads are their own action, saved immediately,
            not part of the form submit. A new product has no id to attach to
            yet, so this only appears once the product exists. */}
        {isEdit && <ArtworkPanel productId={id!} canEdit={canEditProducts} />}

        <div className="flex justify-end gap-2">
          <Link to={listReturnUrl('/products')} className="btn-ghost">
            {canEditProducts ? 'Cancel' : 'Back'}
          </Link>
          {canEditProducts && (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

/** Pull the GSM out of a sheet label like "571.5 x 990.6 (290)" → "290". */
function gsmFromSheet(sheetSize: string): string {
  const m = /\((\d+(?:\.\d+)?)\)\s*$/.exec(sheetSize ?? '');
  return m ? m[1]! : '';
}

interface Artwork {
  id: number; fileName: string; contentType: string;
  sizeBytes: number; uploadedOn: string; url: string;
}

const ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';

function fileSize(n: number): string {
  return n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB`
       : n >= 1024        ? `${Math.round(n / 1024)} KB`
       : `${n} B`;
}

/**
 * Artwork attached to a product — JPG, PNG or PDF.
 *
 * Uploads go straight to the API rather than riding on the form submit: a file
 * is not part of the product record, and making someone press Update to commit
 * a file they already chose is how files get lost.
 */
function ArtworkPanel({ productId, canEdit }: { productId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const list = useQuery({
    queryKey: ['product-artwork', productId],
    queryFn: () => api<Artwork[]>(`/api/products/${encodeURIComponent(productId)}/artwork`),
  });

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setErr(null); setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', f);
        // No content-type header — the browser must set the multipart boundary.
        await api(`/api/products/${encodeURIComponent(productId)}/artwork`, { method: 'POST', body: fd });
      }
      qc.invalidateQueries({ queryKey: ['product-artwork', productId] });
    } catch (e) {
      const m = (e as Error).message;
      setErr(
        m === 'unsupported_type' ? 'Only JPG, PNG and PDF files can be attached.'
        : m.startsWith('file_too_large:') ? `That file is too large — the limit is ${m.split(':')[1]} MB.`
        : m === 'empty_file' ? 'That file is empty.'
        : m === 'product_not_found' ? 'Save the product first, then attach artwork.'
        : m === 'forbidden_role' ? 'Your role cannot change product artwork.'
        : m,
      );
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/products/artwork/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-artwork', productId] }),
    onError: (e) => setErr((e as Error).message),
  });

  const rows = list.data ?? [];

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <span>
          Artwork
          <span className="ml-2 text-xs font-normal text-slate-500">JPG, PNG or PDF</span>
        </span>
        {canEdit && (
          <label className={`btn-primary text-xs py-1 cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
            {busy ? 'Uploading…' : 'Attach file'}
            <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden"
                   onChange={(e) => upload(e.target.files)} />
          </label>
        )}
      </div>

      {err && <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      <div className="card-body">
        {list.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {!list.isLoading && rows.length === 0 && (
          <div className="text-sm text-slate-500">
            No artwork attached yet.
            {canEdit && ' Use “Attach file” above.'}
          </div>
        )}
        {rows.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {rows.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2">
                <span className="text-lg" aria-hidden>
                  {a.contentType === 'application/pdf' ? '📄' : '🖼️'}
                </span>
                <div className="min-w-0 flex-1">
                  <a href={a.url} target="_blank" rel="noreferrer"
                     className="text-sm text-brand hover:underline break-all">
                    {a.fileName}
                  </a>
                  <div className="text-[11px] text-slate-500">
                    {fileSize(a.sizeBytes)}
                    {a.uploadedOn && ` · ${new Date(a.uploadedOn).toLocaleDateString('en-GB',
                      { day: '2-digit', month: 'short', year: 'numeric' })}`}
                  </div>
                </div>
                {canEdit && (
                  <button type="button"
                          className="btn-ghost text-xs py-1 text-red-600"
                          disabled={remove.isPending}
                          onClick={() => { setErr(null); remove.mutate(a.id); }}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">{title}</div>
      <div className="card-body grid gap-4 md:grid-cols-3">{children}</div>
    </div>
  );
}

function F({ label, value, onChange, autoFocus }:
  { label: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus} />
    </div>
  );
}

function Sel({ label, value, onChange, options }:
  { label: string; value: string; onChange: (v: string) => void; options: LookupOption[] }) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        {value && !options.find((o) => o.id === value) && <option value={value}>{value} (legacy)</option>}
      </select>
    </div>
  );
}

/**
 * Sheet Size dropdown filtered by the currently selected Paper Type.
 * If no paper type is picked yet, the field is disabled with a hint.
 */
function SheetPicker({ paperType, value, onChange }:
  { paperType: string; value: string; onChange: (v: string) => void }) {
  const q = useQuery({
    enabled: !!paperType,
    queryKey: ['sheets-for-paper', paperType],
    queryFn: () => api<Array<{ id: string; name: string; length: string | null; width: string | null; gsm: string | null }>>(
      `/api/lookups/sheets?paperType=${encodeURIComponent(paperType)}`,
    ),
    // Short stale window + always refetch on mount so a sheet added a few
    // seconds ago in another tab shows up immediately when the operator
    // switches to the Product form.
    staleTime: 5_000,
    refetchOnMount: 'always',
  });
  const options = (q.data ?? []).map((s) => {
    // Sheets are stored in mm in tblSheetMaster — display as-is.
    const label = `${s.length ?? ''} x ${s.width ?? ''}${s.gsm ? ` (${s.gsm})` : ''}`;
    return { value: label, label };
  });
  const hasCurrentAsOption = value === '' || options.some((o) => o.value === value);
  return (
    <div>
      <label className="label">Full Sheet Size — L × W (GSM), mm</label>
      <select className="input" value={value} disabled={!paperType}
        onChange={(e) => onChange(e.target.value)}>
        <option value="">{paperType ? (q.isLoading ? 'Loading…' : '—') : 'Pick a Paper Type first'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        {!hasCurrentAsOption && (
          <option value={value}>{value} (legacy)</option>
        )}
      </select>
      {paperType && !q.isLoading && options.length === 0 && (
        <p className="mt-1 text-xs text-amber-700">
          No sheets in Sheet Master use this paper type. Add one under Master
          Database → Sheet.
        </p>
      )}
    </div>
  );
}

function Bools({ form, update }: { form: ProductForm; update: <K extends keyof ProductForm>(k: K, v: ProductForm[K]) => void }) {
  const items: Array<[keyof ProductForm, string]> = [
    ['onlineEmbossing', 'Online Embossing'], ['offlineEmbossing', 'Offline Embossing'],
    ['braille', 'Braille'], ['spotUv', 'Spot UV'], ['screenUv', 'Screen UV'],
    ['offlineCoating', 'Offline Coating'],
    ['onlineCoating', 'Online Coating'],
    ['baseCoat', 'Base Coat'],
  ];
  return (
    <div className="md:col-span-3 grid gap-2 sm:grid-cols-3">
      {items.map(([key, label]) => (
        <label key={key} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={Boolean(form[key])}
            onChange={(e) => update(key, e.target.checked as ProductForm[typeof key])} />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}
